import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { FeatureFlagService } from "../feature-flag.service";
import { InteractionTrackingService } from "../interaction-tracking.service";
import { NovaCompanionSettingsService } from "../nova-companion-settings.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { LearnOrientationPrompt } from "./learn-orientation-lesson";
import { NovaChatLesson } from "./nova-chat-lesson";

export const N03A_LESSON_ID = "N03A";

/** Recorded with N03A's completed lesson state - N03A's CompletedAt *is* this milestone. */
export const NOVA_DICTATION_INTRODUCED = "nova_dictation_introduced";

/** Anchors workbook: the message-field microphone. */
export const N03A_ANCHOR = "nova.dictation";

/** Copy rows N03A.01-N03A.02, workbook v2.38 Copy sheet (verbatim). */
export const N03A_COPY = {
  heading: "Dictate a message",
  body:
    "Speak instead of typing and Nova will turn your words into a written message. You can review or " +
    "change your message before sending.",
};

/**
 * Onboarding lesson N03A "Dictate a message" (FLIP-390; spec v2.36 §24.4, workbook v2.38): an
 * informational anchored prompt at the message-field microphone. Flow shared with the other chat
 * lessons - see NovaChatLesson.
 *
 * - Shows in the chat as soon as N02's first-use panel has been closed (or straight away when N02 is
 *   already resolved) - the chat calls setReady(true) - and is followed automatically by N03B. It is
 *   not triggered by using the microphone, and never starts dictation itself: dismissing it cannot
 *   have started recording.
 * - Completes on the visible close, or on genuine dictation use (`onTargetUsed("dictation")`).
 *   Standard informational dismissal completes permanently.
 */
@Injectable({ providedIn: "root" })
export class N03ADictateMessageService extends NovaChatLesson {
  protected readonly stepKey = N03A_LESSON_ID;
  protected readonly milestone = NOVA_DICTATION_INTRODUCED;

  constructor(
    router: Router,
    coachMarkService: CoachMarkService,
    blockingUiSignal: BlockingUiSignalService,
    featureFlagService: FeatureFlagService,
    paymentsService: PaymentsService,
    onboardingStepService: OnboardingStepService,
    interactionTracking: InteractionTrackingService,
    companionSettings: NovaCompanionSettingsService
  ) {
    super(
      router,
      coachMarkService,
      blockingUiSignal,
      featureFlagService,
      paymentsService,
      onboardingStepService,
      interactionTracking,
      companionSettings
    );
  }

  protected prompt(): LearnOrientationPrompt | null {
    // The composer sits at the foot of the sheet, so the prompt stays above the microphone.
    return this.companionOn
      ? { lessonId: N03A_LESSON_ID, target: N03A_ANCHOR, lockedSide: "above", ...N03A_COPY }
      : null;
  }
}
